import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { ResourceRecord } from '@/settings/components/resources/ResourceCard';
import { resourcesListQueryKey } from './use-resources-list';

export interface ResourceArchiveInput {
  id: string;
  /** Only used to target the cache key for invalidation. */
  practiceId: string;
}

/**
 * Archive one resource — PROD-2010.
 *
 *   POST /api/Resource/{id}/Archive   (no body)
 *
 * Archive is the ONLY form of removal: there is no delete route, the name stays
 * reserved, and booked history keeps the resource. Idempotent server-side, so a
 * double click or a retried request is a no-op rather than a 400.
 */
export function useArchiveResource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ResourceArchiveInput) =>
      post<ResourceRecord>(`/api/Resource/${input.id}/Archive`, undefined),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: resourcesListQueryKey(vars.practiceId),
      });
    },
  });
}

/**
 * Restore an archived resource — PROD-2010.
 *
 *   POST /api/Resource/{id}/Restore   (no body)
 *
 * The reverse of Archive, and equally idempotent.
 */
export function useRestoreResource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ResourceArchiveInput) =>
      post<ResourceRecord>(`/api/Resource/${input.id}/Restore`, undefined),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: resourcesListQueryKey(vars.practiceId),
      });
    },
  });
}
